// Workstream B #34 — the leaf narrowing for the kinds the identifier index
// answers with its EXACT-match `query` (columns and routines), split out of
// `relationIdentity.ts`, which keeps the overview and still exports
// {@link catalogLeafRefMatches} to every caller. The relation-candidate half of
// the same decision is `relationIdentity.ts`'s `matchRelationRef`.

import {
  catalogComponentEq,
  leafComponent,
  schemaComponent,
  type IdentityComponent,
  type RelationIdentityContext,
} from './identityCaseContext';
import type { IdentifierKind, QualifiedName } from './types';

/** The identity component a leaf of `kind` is compared under. Only MySQL reads
 *  the difference (a routine name and a column name are not filesystem entries,
 *  so `lower_case_table_names` never reaches them); every other engine answers
 *  all three the same way. */
function leafComponentKind(kind: IdentifierKind): IdentityComponent {
  if (kind === 'routine') return 'routine-leaf';
  if (kind === 'column') return 'column-leaf';
  return 'leaf';
}

/** Per-engine equality for two COLUMN leaves — the comparison every caller that
 *  is asking "is this token the renamed column?" must use, rather than
 *  `relationIdentity.ts`'s {@link relationIdentitiesEqual}, whose leaf is a
 *  RELATION leaf.
 *
 *  The two differ on exactly one engine. On MySQL with
 *  `@@lower_case_table_names = 0` a relation leaf is case-SIGNIFICANT (a table
 *  is a file on disk), while a column name — which lives inside the table's
 *  definition, not on the filesystem — is case-insensitive in every mode, the
 *  same rule `mysqlCaseProbe.ts`'s `mysqlObjectKeyCase` already keys the index
 *  with. Comparing a column leaf as a relation leaf therefore left an open tab's
 *  valid `u.ID` unrewritten when `users.id` was renamed.
 *
 *  Every other engine is unchanged and deliberately so: PostgreSQL / Oracle
 *  compare the canonical folded forms (`"Id"` and `id` stay two columns),
 *  SQLite is blanket case-insensitive, and SQL Server compares a column in the
 *  collation of the database that holds it — a `_CS_` database really does tell
 *  `Email` from `email`, so it keeps the SPELLED compare
 *  ({@link catalogComponentEq} answers the mssql branch before MySQL's). */
export function columnLeavesEqual(
  a: QualifiedName,
  b: QualifiedName,
  ctx: RelationIdentityContext,
): boolean {
  return catalogComponentEq(leafComponent(a), leafComponent(b), ctx, 'column-leaf');
}

/** Whether the exact-match lookup's answer needs narrowing on this engine.
 *
 *  The lookup key is the LOWERCASED folded component (`index/identifierIndex.ts`'s
 *  `foldedKey`) — a discovery key, coarse on purpose. Whether that key is
 *  coarser than the engine's real identity depends on the engine:
 *
 *   - PostgreSQL / Oracle: the FOLDED form is the identity (unquoted folds
 *     lower / upper, quoted preserves case), and lowercasing it throws away a
 *     real distinction — pg's `id` and `"Id"` are two columns that share one
 *     bucket. Narrow, always.
 *   - SQL Server: names are compared in the collation of the database that
 *     holds them, so only a `_CS_` database is finer than the key. There the
 *     comparison runs on the SPELLED components (see `identityCaseContext.ts`'s
 *     {@link catalogComponentEq}).
 *   - SQLite / MySQL: identifiers are case-insensitive (MySQL's
 *     `lower_case_table_names` governs table and database names, not columns,
 *     and parse-time folding already lowercases them), so the lowercased key
 *     IS the identity. Narrowing here would drop real references. */
function catalogLeafNarrows(ctx: RelationIdentityContext): boolean {
  switch (ctx.engine) {
    case 'postgres':
    case 'oracle':
      return true;
    case 'mssql':
      return ctx.mssqlCatalogCaseSensitive === true;
    default:
      return false;
  }
}

/** The final identity decision for the kinds the index answers with its
 *  EXACT-match `query` — columns and routines — narrowing that lookup's
 *  deliberately broad answer the same way `relationIdentity.ts`'s
 *  `matchRelationRef` narrows the relation-candidate lookup. See
 *  {@link catalogLeafNarrows} for which engines need it and why; a no-op
 *  (returns true) on the ones that do not.
 *
 *  Concretely it drops a case-sensitive SQL Server database's `dbo.p` from a
 *  search for `dbo.P`, and PostgreSQL's `"Id"` from a rename of `id`. */
export function catalogLeafRefMatches(
  ref: QualifiedName,
  target: QualifiedName,
  kind: IdentifierKind,
  ctx: RelationIdentityContext,
): boolean {
  if (!catalogLeafNarrows(ctx)) return true;
  // The leaf is compared under its OWN kind's component, so the one engine that
  // reads the difference (MySQL) cannot be broken by a later change to
  // {@link catalogLeafNarrows}: today it returns false there, but the part no
  // longer has to be re-derived if that ever changes.
  const part = leafComponentKind(kind);
  if (!catalogComponentEq(leafComponent(ref), leafComponent(target), ctx, part)) return false;
  // Columns are recorded bare (`email`), so only a ROUTINE carries a schema
  // worth comparing; a component absent on either side stays a wildcard, as
  // everywhere else in this module.
  if (kind !== 'routine') return true;
  const refSchema = schemaComponent(ref);
  const targetSchema = schemaComponent(target);
  if (!refSchema.spelled || !targetSchema.spelled) return true;
  return catalogComponentEq(refSchema, targetSchema, ctx);
}
